import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import ReactQuill from "react-quill";
import GlobalButton from "../../../common/GlobalButton";
import UpdateIcon from "@mui/icons-material/Update";
import BackWithArrow from "../../../common/BackWithArrow";
import { fetchFullDataQMF } from "../../../../api/fetchFullDataQMF";
import { updateFullQMF } from "../../../../utils/UpdateFullQMF"; // Import the update function
import Swal from "sweetalert2";
import { formatDate } from "../../../../utils/formatDate"; // Import date formatting utility
//
function FullyEditButton() {
  const [open, setOpen] = useState(false);
  const [qmfData, setQmfData] = useState(null);
  // Add notification state
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info", // 'error', 'warning', 'info', 'success'
  });

  // Add state for each field
  const [revisionNo, setRevisionNo] = useState("");
  const [revisionDate, setRevisionDate] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [normativeReferences, setNormativeReferences] = useState("");
  const [termsDefinitions, setTermsDefinitions] = useState("");
  const [understandingOrg, setUnderstandingOrg] = useState("");
  const [needsExpectations, setNeedsExpectations] = useState("");
  const [scopeQMS, setScopeQMS] = useState("");
  const [qmsProcesses, setQmsProcesses] = useState("");
  const [leadershipCommitment, setLeadershipCommitment] = useState("");
  const [qualityPolicy, setQualityPolicy] = useState("");
  const [orgRoles, setOrgRoles] = useState("");
  const [actionsRisks, setActionsRisks] = useState("");
  const [qualityObjectives, setQualityObjectives] = useState("");
  const [resources, setResources] = useState("");
  const [documentedInfo, setDocumentedInfo] = useState("");
  const [customerCommunication, setCustomerCommunication] = useState("");
  const [propertyCustomers, setPropertyCustomers] = useState("");
  const [preservation, setPreservation] = useState("");
  const [releaseProducts, setReleaseProducts] = useState("");
  const [controlNonconforming, setControlNonconforming] = useState("");
  const [monitoring, setMonitoring] = useState("");
  const [managementReview, setManagementReview] = useState("");
  const [generalImprovement, setGeneralImprovement] = useState("");
  const [nonconformity, setNonconformity] = useState("");
  const [continualImprovement, setContinualImprovement] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Fetch and set QMF data to fields
  useEffect(() => {
    if (open) {
      fetchFullDataQMF().then((data) => {
        const d = data && data.length > 0 ? data[0] : null;
        setQmfData(d);
        // Map API response fields to state
        if (d) {
          setRevisionNo(d.Revision_No || "");
          // Use the original date value directly if it's in the correct format
          const originalDate = d.Revision_Date; // e.g., "09-May-2025"
          if (originalDate) {
            // Convert "09-May-2025" to "2025-05-09" for the input field
            const [day, month, year] = originalDate.split("-");
            const monthIndex = new Date(`${month} 1, 2000`).getMonth() + 1; // Get month index
            const formattedDate = `${year}-${String(monthIndex).padStart(
              2,
              "0"
            )}-${day}`;
            setRevisionDate(formattedDate);
          } else {
            setRevisionDate(""); // Clear the field if no date is available
          }
          //
          //setRevisionDate(d.Revision_Date || "");
          setIntroduction(d.Introduction || "");
          setNormativeReferences(d.Normative_References || "");
          setTermsDefinitions(d.Terms_and_Definitions || "");
          setUnderstandingOrg(d.Understanding_the_Organization || "");
          setNeedsExpectations(
            d.Understanding_the_Needs_and_Expectations_of_Interested_Parties ||
              ""
          );
          setScopeQMS(
            d.Determining_the_Scope_of_the_Quality_Management_System || ""
          );
          setQmsProcesses(d.Quality_Management_System_and_Its_Processes || "");
          setLeadershipCommitment(d.Leadership_Commitment || "");
          setQualityPolicy(d.Quality_Policy || "");
          setOrgRoles(
            d.Organizational_Roles_responsibilities_and_authorities || ""
          );
          setActionsRisks(d.Actions_to_address_risks_and_opportunities || "");
          setQualityObjectives(
            d.Quality_objectives_and_planning_to_achieve_them || ""
          );
          setResources(
            d.Resources_including_subpoints_like_people_infrastructure_etc || ""
          );
          setDocumentedInfo(d.Documented_information || "");
          setCustomerCommunication(d.Customer_communication || "");
          setPropertyCustomers(
            d.Property_belonging_to_customers_or_external_providers || ""
          );
          setPreservation(d.Preservation || "");
          setReleaseProducts(d.Release_of_products_and_services || "");
          setControlNonconforming(d.Control_of_nonconforming_outputs || "");
          setMonitoring(d.Monitoring_measurement_analysis_and_evaluation || "");
          setManagementReview(d.Management_review || "");
          setGeneralImprovement(d.General || d.General1 || "");
          setNonconformity(d.Nonconformity_and_corrective_action || "");
          setContinualImprovement(d.Continual_improvement || "");
        }
      });
    }
  }, [open]);

  // Handle update button click
  const handleUpdateManual = async () => {
    // Format the Effective_Date to dd-MMM-yyyy before sending to the API
    const formattedEffectiveDate = formatDate(new Date(revisionDate));

    if (!formattedEffectiveDate) {
      alert("Enter a valid date format for Effective Date");
      return;
    }

    // Prepare the data for update
    const updateData = {
      Revision_No: revisionNo,
      Revision_Date: formattedEffectiveDate,
      Introduction: introduction,
      Normative_References: normativeReferences,
      Terms_and_Definitions: termsDefinitions,
      Understanding_the_Organization: understandingOrg,
      Understanding_the_Needs_and_Expectations_of_Interested_Parties:
        needsExpectations,
      Determining_the_Scope_of_the_Quality_Management_System: scopeQMS,
      Quality_Management_System_and_Its_Processes: qmsProcesses,
      Leadership_Commitment: leadershipCommitment,
      Quality_Policy: qualityPolicy,
      Organizational_Roles_responsibilities_and_authorities: orgRoles,
      Actions_to_address_risks_and_opportunities: actionsRisks,
      Quality_objectives_and_planning_to_achieve_them: qualityObjectives,
      Resources_including_subpoints_like_people_infrastructure_etc: resources,
      Documented_information: documentedInfo,
      Customer_communication: customerCommunication,
      Property_belonging_to_customers_or_external_providers: propertyCustomers,
      Preservation: preservation,
      Release_of_products_and_services: releaseProducts,
      Control_of_nonconforming_outputs: controlNonconforming,
      Monitoring_measurement_analysis_and_evaluation: monitoring,
      Management_review: managementReview,
      General: generalImprovement,
      General1: generalImprovement, // Both fields appear to be used in the API
      Continual_improvement: continualImprovement,
    };

    // Get the recordId from the qmfData
    const recordId = qmfData?.ID;

    if (!recordId) {
      setNotification({
        open: true,
        message: "Record ID not found. Cannot update manual.",
        severity: "error",
      });
      return;
    }

    // Call the update function
    const result = await updateFullQMF(recordId, updateData);

    // Show notification based on result
    setNotification({
      open: true,
      message: result.message,
      severity: result.severity,
    });

    if (result.success) {
      // Close the dialog on success after a short delay
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
  };

  return (
    <>
      <Tooltip title="Fully update" arrow>
        <GlobalButton
          sx={{
            minWidth: 80,
            height: 30,
            px: 2,
            py: 0.5,
          }}
          onClick={handleOpen}
          icon={<UpdateIcon />}
        >
          Fully update
        </GlobalButton>
      </Tooltip>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <BackWithArrow onBack={handleClose} />
        <DialogContent>
          {/* Removed debug <pre> block */}
          {/* Static HTML for all fields and sections */}
          <div style={{ display: "flex", gap: "16px", paddingTop: "15px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Revision No
              </label>
              <input
                type="text"
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
                placeholder="Revision No"
                value={revisionNo}
                onChange={(e) => setRevisionNo(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                Revision Date
              </label>
              <input
                type="date"
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
                placeholder="Select revision date"
                value={revisionDate}
                onChange={(e) => setRevisionDate(e.target.value)}
              />
            </div>
          </div>
          <div style={{ paddingTop: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px" }}>
              Introduction
            </label>
            <ReactQuill
              theme="snow"
              value={introduction}
              onChange={setIntroduction}
              style={{ marginBottom: "16px" }}
            />
          </div>
          <div style={{ paddingTop: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px" }}>
              Normative References
            </label>
            <ReactQuill
              theme="snow"
              value={normativeReferences}
              onChange={setNormativeReferences}
              style={{ marginBottom: "16px" }}
            />
          </div>
          <div style={{ paddingTop: "15px" }}>
            <label style={{ display: "block", marginBottom: "8px" }}>
              Terms and Definitions
            </label>
            <ReactQuill
              theme="snow"
              value={termsDefinitions}
              onChange={setTermsDefinitions}
              style={{ marginBottom: "16px" }}
            />
          </div>
          {/* Context of the Organization */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>
              Context of the Organization
            </h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                4.1 Understanding the Organization
              </label>
              <ReactQuill
                theme="snow"
                value={understandingOrg}
                onChange={setUnderstandingOrg}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                4.2 Understanding the Needs and Expectations of Interested
                Parties
              </label>
              <ReactQuill
                theme="snow"
                value={needsExpectations}
                onChange={setNeedsExpectations}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                4.3 Determining the Scope of the Quality Management System
              </label>
              <ReactQuill
                theme="snow"
                value={scopeQMS}
                onChange={setScopeQMS}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                4.4 Quality Management System and Its Processes
              </label>
              <ReactQuill
                theme="snow"
                value={qmsProcesses}
                onChange={setQmsProcesses}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Leadership */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Leadership</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                5.1 Leadership Commitment
              </label>
              <ReactQuill
                theme="snow"
                value={leadershipCommitment}
                onChange={setLeadershipCommitment}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                5.2 Quality Policy
              </label>
              <ReactQuill
                theme="snow"
                value={qualityPolicy}
                onChange={setQualityPolicy}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                5.3 Organizational Roles, responsibilities, and authorities
              </label>
              <ReactQuill
                theme="snow"
                value={orgRoles}
                onChange={setOrgRoles}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Planning */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Planning</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                6.1 Actions to address risks and opportunities
              </label>
              <ReactQuill
                theme="snow"
                value={actionsRisks}
                onChange={setActionsRisks}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                6.2 Quality objectives and planning to achieve them
              </label>
              <ReactQuill
                theme="snow"
                value={qualityObjectives}
                onChange={setQualityObjectives}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Support */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Support</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                7.1 Resources (including subpoints like people, infrastructure,
                etc.)
              </label>
              <ReactQuill
                theme="snow"
                value={resources}
                onChange={setResources}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                7.5 Documented information
              </label>
              <ReactQuill
                theme="snow"
                value={documentedInfo}
                onChange={setDocumentedInfo}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Operation */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Operation</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                8.0 Customer communication
              </label>
              <ReactQuill
                theme="snow"
                value={customerCommunication}
                onChange={setCustomerCommunication}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                8.3 Property belonging to customers or external providers
              </label>
              <ReactQuill
                theme="snow"
                value={propertyCustomers}
                onChange={setPropertyCustomers}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                8.4 Preservation
              </label>
              <ReactQuill
                theme="snow"
                value={preservation}
                onChange={setPreservation}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                8.6 Release of products and services
              </label>
              <ReactQuill
                theme="snow"
                value={releaseProducts}
                onChange={setReleaseProducts}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                8.7 Control of nonconforming outputs
              </label>
              <ReactQuill
                theme="snow"
                value={controlNonconforming}
                onChange={setControlNonconforming}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Performance Evaluation */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Performance Evaluation</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                9.1 Monitoring, measurement, analysis, and evaluation
              </label>
              <ReactQuill
                theme="snow"
                value={monitoring}
                onChange={setMonitoring}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                9.3 Management review
              </label>
              <ReactQuill
                theme="snow"
                value={managementReview}
                onChange={setManagementReview}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
          {/* Improvement */}
          <div style={{ marginTop: "20px" }}>
            <h3 style={{ marginBottom: "10px" }}>Improvement</h3>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                10.1 General
              </label>
              <ReactQuill
                theme="snow"
                value={generalImprovement}
                onChange={setGeneralImprovement}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                10.2 Nonconformity and corrective action
              </label>
              <ReactQuill
                theme="snow"
                value={nonconformity}
                onChange={setNonconformity}
                style={{ marginBottom: "16px" }}
              />
            </div>
            <div style={{ paddingTop: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px" }}>
                10.3 Continual improvement
              </label>
              <ReactQuill
                theme="snow"
                value={continualImprovement}
                onChange={setContinualImprovement}
                style={{ marginBottom: "16px" }}
              />
            </div>
          </div>
        </DialogContent>
        <div
          style={{
            padding: "16px",
            textAlign: "center",
            borderTop: "1px solid #ccc",
          }}
        >
          <GlobalButton
            sx={{
              minWidth: 100,
              height: 46,
              px: 3,
              py: 2.5,
            }}
            onClick={handleUpdateManual} // Add onClick handler
            icon={<UpdateIcon />}
          >
            Update Manual
          </GlobalButton>
        </div>
      </Dialog>

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default FullyEditButton;
